import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDateTimePicker } from '@/components/triniti-datetime';
import { format } from 'date-fns';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const TYPES = ['INJURY','MEDICATION','BEHAVIOUR','RESTRICTIVE_PRACTICE','MISSING_PERSON','PROPERTY_DAMAGE','ABUSE_NEGLECT','WORKPLACE_HEALTH_SAFETY','OTHER'];
const SEVERITIES = ['LOW','MEDIUM','HIGH','CRITICAL'];

export default function IncidentCreatePage() {
  const navigate = useNavigate();
  // client#10: opened from the clock checklist → participant / shift / location pre-filled, back to the clock afterwards
  const [params] = useSearchParams();
  const returnTo = (params.get('return_to') || '').startsWith('/') && !(params.get('return_to') || '').startsWith('//') ? params.get('return_to') : null;
  const prefillName = params.get('participant_name') || '';
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    participant_id: params.get('participant_id') || '', reported_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),   // local wall clock — the UTC slice prefilled the previous day on NSW mornings
    ...(params.get('shift_id') ? { shift_id: params.get('shift_id') } : {}),
    location: params.get('location') || '', incident_type: 'INJURY', severity: 'MEDIUM',
    description: '', immediate_actions_taken: '',
    emergency_services_contacted: false, hospital_visit: false,
    police_reference_no: '',
  });

  useEffect(() => {
    api.get('/participants?pageSize=100').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e, submitAfter = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/incidents', { ...form, reported_at: new Date(form.reported_at).toISOString() });
      const incId = res.data.data.id;
      if (submitAfter) {
        await api.post(`/incidents/${incId}/submit`);
        toast.success('Incident submitted');
      } else {
        toast.success('Incident saved as draft');
      }
      navigate(returnTo || `/compliance/incidents/${incId}`);
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed to create'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(returnTo || '/compliance/incidents')} className="-ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <h1 className="text-2xl font-bold tracking-tight">Report New Incident</h1>

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Incident Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Participant</Label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  value={form.participant_id}
                  onChange={(v) => setForm({...form, participant_id: v})}
                  getOptionLabel={participantLabel}
                  selectedLabel={labelFor(participants, form.participant_id, participantLabel) || (form.participant_id === params.get('participant_id') ? prefillName : '')}
                  placeholder="Select"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="inc-participant"
                />
              </div>
              <div className="space-y-2"><Label>Date/Time</Label><TrinitiDateTimePicker value={form.reported_at} onChange={(v) => setForm({...form, reported_at: v})} required /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.incident_type} onValueChange={(v) => setForm({...form, incident_type: v})}>
                  <SelectTrigger data-testid="inc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                  <SelectTrigger data-testid="inc-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="e.g. Participant home" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><textarea className="w-full min-h-[100px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required data-testid="inc-description" /></div>
            <div className="space-y-2"><Label>Immediate Actions Taken</Label><textarea className="w-full min-h-[80px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.immediate_actions_taken} onChange={(e) => setForm({...form, immediate_actions_taken: e.target.value})} /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.emergency_services_contacted} onCheckedChange={(v) => setForm({...form, emergency_services_contacted: !!v})} /> Emergency services contacted</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.hospital_visit} onCheckedChange={(v) => setForm({...form, hospital_visit: !!v})} /> Hospital visit</label>
            </div>
            {form.emergency_services_contacted && <div className="space-y-2"><Label>Police Reference No.</Label><Input value={form.police_reference_no} onChange={(e) => setForm({...form, police_reference_no: e.target.value})} /></div>}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" variant="outline" disabled={loading} data-testid="save-draft">Save as Draft</Button>
          <Button type="button" onClick={(e) => handleSubmit(e, true)} disabled={loading} data-testid="save-submit">{loading ? 'Saving...' : 'Save & Submit'}</Button>
        </div>
      </form>
    </div>
  );
}
